import { useMemo, type ReactNode } from 'react';
import { EMPTY_VALUE } from '@/constants/app.constants';
import {
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { formatNumber, formatTaka } from '@/utils/number';
import type {
  PcmpProposalStudentClass,
  PcmpSubmissionCalculation,
} from '../../types/pcmpProposal.types';
import { buildPcmpStudentGrid } from '../../utils/pcmpStudentGrid';
import { PcmpProposalSection } from './PcmpProposalSection';

interface PcmpStudentSummaryCardProps {
  students: PcmpProposalStudentClass[];
  calculation: PcmpSubmissionCalculation;
  actions?: ReactNode;
}

const NUMBER_CELL = 'text-right tabular-nums';

export function PcmpStudentSummaryCard({
  students,
  calculation,
  actions,
}: PcmpStudentSummaryCardProps) {
  const grid = useMemo(() => buildPcmpStudentGrid(students, calculation), [students, calculation]);

  return (
    <PcmpProposalSection title="Student Summary" actions={actions}>
      {grid.rows.length === 0 ? (
        <p className="text-sm text-muted-foreground">No classes on this submission.</p>
      ) : (
        <div className="overflow-x-auto">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Class</TableHead>
                <TableHead className="text-right">Students</TableHead>
                {grid.categories.map((category) => (
                  <TableHead key={category.categoryId} className="text-right">
                    {category.categoryName} qty
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {grid.rows.map((row) => (
                <TableRow key={row.classId}>
                  <TableCell className="font-medium">{row.className}</TableCell>
                  <TableCell className={NUMBER_CELL}>{formatNumber(row.studentQty)}</TableCell>
                  {grid.categories.map((category) => {
                    const book = row.booksByCategory.get(category.categoryId);
                    return (
                      <TableCell key={category.categoryId} className={NUMBER_CELL}>
                        {book ? formatNumber(book.tentativePurchaseQty) : EMPTY_VALUE}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TableCell className="font-semibold">Total</TableCell>
                <TableCell className={NUMBER_CELL}>{formatNumber(grid.totalStudents)}</TableCell>
                {grid.categories.map((category) => (
                  <TableCell key={category.categoryId} className={NUMBER_CELL}>
                    {formatNumber(grid.totalsByCategory.get(category.categoryId) ?? 0)}
                  </TableCell>
                ))}
              </TableRow>
              <TableRow>
                <TableCell className="font-semibold">Sales target</TableCell>
                <TableCell className={NUMBER_CELL}>
                  {formatTaka(calculation.totalSalesTarget)}
                </TableCell>
                {grid.categories.map((category) => (
                  <TableCell key={category.categoryId} className={NUMBER_CELL}>
                    {category.salesTarget === null ? EMPTY_VALUE : formatTaka(category.salesTarget)}
                  </TableCell>
                ))}
              </TableRow>
            </TableFooter>
          </Table>
        </div>
      )}
    </PcmpProposalSection>
  );
}
